import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Skeleton,
} from "@mui/material";
import { useContext, useMemo, useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useTable, useSortBy, Column } from "react-table";
import DashboardService from "../../../services/dashboard.service";
import { useDashboard } from "../../../contexts/DashboardContext";
import { FaSearch } from "react-icons/fa";
import { ProjectSortField, SortOrder } from "../../../types/dashboard";
import { BsChevronDown, BsChevronUp, BsChevronExpand } from "react-icons/bs";
import { styled, keyframes } from "@mui/material/styles";
import { formatCurrency, formatCurrencyShort } from "../../../utils/format";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

// Keyframe para efeito de shimmer
const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

// Componente de Skeleton personalizado com efeito de shimmer
const ShimmerSkeleton = styled(Box)(({ theme }) => ({
  display: "inline-block",
  position: "relative",
  backgroundColor: "#f0f0f0",
  backgroundImage: "linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%)",
  backgroundSize: "800px 104px",
  animation: `${shimmer} 1.5s linear infinite`,
  borderRadius: 4,
}));

interface IEventRow {
  id: string;
  mmCode: number;
  yearCode: number;
  eventName: string;
  clientName: string;
  date?: string;
  audience?: number;
  billing?: number;
  revenue?: number;
}

export const CEOEventsList = () => {
  const { currentFilters, loading: dashboardLoading, setFilteredProjectsTotal } = useDashboard();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [localSortBy, setLocalSortBy] = useState<string | undefined>();
  const [localSortOrder, setLocalSortOrder] = useState<string | undefined>();

  // Estabiliza a chave de query com primitivas extraídas dos filtros
  const stableFilterKey = useMemo(() => JSON.stringify({
    cellId: currentFilters.cellId,
    clientId: currentFilters.clientId,
    collaboratorId: currentFilters.collaboratorId,
    startDate: currentFilters.startDate,
    endDate: currentFilters.endDate,
    status: currentFilters.status,
  }), [
    currentFilters.cellId,
    currentFilters.clientId,
    currentFilters.collaboratorId,
    currentFilters.startDate?.start,
    currentFilters.startDate?.end,
    currentFilters.endDate?.start,
    currentFilters.endDate?.end,
    currentFilters.status?.join(","),
  ]);

  // Implementar debounce de 3 segundos
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 3000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const { isLoading, data, refetch } = useQuery<IEventRow[]>(
    ["dashboard-events", stableFilterKey, debouncedSearchTerm, localSortBy, localSortOrder],
    () => {
      setIsLocalLoading(true);
      return DashboardService.getProjects({
        ...currentFilters,
        searchQuery: debouncedSearchTerm || undefined,
        includeExtraFields: true, // Sempre solicitar campos extras para o CEO
        sortBy: localSortBy as ProjectSortField,
        sortOrder: localSortOrder as SortOrder
      })
        .then((res) => {
          setIsLocalLoading(false);
          return res;
        })
        .catch((error) => {
          setIsLocalLoading(false);
          throw error;
        });
    },
    {
      enabled: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  // Determinar se existem filtros ativos
  const hasActiveFilters = useMemo(() => {
    return (
      debouncedSearchTerm.trim() !== "" ||
      Object.keys(currentFilters).some((key) => {
        const value = currentFilters[key as keyof typeof currentFilters];
        if (Array.isArray(value) && value.length > 0) return true;
        if (value && typeof value === "object") {
          if ("start" in value && value.start) return true;
          if ("end" in value && value.end) return true;
        }
        return !!value;
      })
    );
  }, [currentFilters, debouncedSearchTerm]);

  useEffect(() => {
    setFilteredProjectsTotal(data?.length ?? null);
  }, [data, setFilteredProjectsTotal]);

  const columns = useMemo<Column<IEventRow>[]>(
    () => [
      {
        Header: "MM",
        accessor: (row) => row.mmCode,
        id: "mmCode",
        Cell: ({ row }: any) => <span>{`${row.original.mmCode}/${row.original.yearCode}`}</span>,
        width: 120,
        canSort: true,
      },
      {
        Header: "Cliente",
        accessor: (row) => row.clientName,
        id: "clientName",
        Cell: ({ row }: any) => <span>{row.original.clientName || "---"}</span>,
        width: 320,
        canSort: true,
      },
      {
        Header: "Nome do evento",
        accessor: (row) => row.eventName,
        id: "eventName",
        Cell: ({ row }: any) => <span>{row.original.eventName || "---"}</span>,
        width: 320,
        canSort: true,
      },
      {
        Header: "Data",
        accessor: (row) => row.date,
        id: "date",
        Cell: ({ value }: { value: any }) => (
          <span>{value ? format(new Date(value), "dd/MM/yyyy", { locale: ptBR }) : "---"}</span>
        ),
        width: 120,
        canSort: true,
      },
      {
        Header: "Público",
        accessor: (row) => row.audience,
        id: "audience",
        width: 120,
        canSort: true,
      },
      {
        Header: "Faturamento",
        accessor: (row) => row.billing,
        id: "billing",
        Cell: ({ value }: { value: any }) => (
          <span>{value ? formatCurrencyShort(value) : "---"}</span>
        ),
        width: 150,
        canSort: true,
      },
      {
        Header: "Receita",
        accessor: (row) => row.revenue,
        id: "revenue",
        Cell: ({ value }: { value: any }) => (
          <span>{value ? formatCurrencyShort(value) : "---"}</span>
        ),
        width: 150,
        canSort: true,
      },
    ],
    []
  );

  const tableData = useMemo(() => data || [], [data]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      columns,
      data: tableData,
      initialState: {
        sortBy: [{ id: "mmCode", desc: true }],
      },
      manualSortBy: true,
      autoResetSortBy: false,
      disableSortRemove: true,
    },
    useSortBy
  );

  // Efeito para enviar os parâmetros de ordenação para a API
  useEffect(() => {
    if (sortBy && sortBy.length > 0) {
      const column = sortBy[0];
      const apiSortBy = column.id as ProjectSortField;
      
      // Alterar para sempre começar com ordenação decrescente (desc)
      // Se a coluna clicada for diferente da atual, defina como desc=true
      // Se for a mesma coluna, alterne a direção
      const isNewColumn = localSortBy !== apiSortBy;
      const apiSortOrder = isNewColumn ? SortOrder.DESC : (column.desc ? SortOrder.DESC : SortOrder.ASC);

      // Usar variáveis de estado locais em vez de modificar diretamente currentFilters
      setLocalSortBy(apiSortBy);
      setLocalSortOrder(apiSortOrder);
      
      // Não precisa chamar refetch explicitamente, a alteração das dependências da query
      // vai disparar automaticamente a refetch
    }
  }, [sortBy, localSortBy]);

  // Componente de ordenação das colunas
  const ColumnSort = ({ column }: { column: any }) => {
    return (
      <span style={{ margin: "8px 3px 0px 3px" }}>
        {column.isSorted ? (
          column.isSortedDesc ? (
            <BsChevronDown />
          ) : (
            <BsChevronUp />
          )
        ) : (
          <BsChevronExpand />
        )}
      </span>
    );
  };

  return (
    <Card sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          pt: 1.5,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
            Eventos
          </Typography>
          <DashboardSourceInfo context="Faturamento e Receita por projeto" />
        </Box>
      </Box>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: "none",
          border: "none",
          maxHeight: "400px",
          minHeight: "300px",
          overflow: "auto",
        }}
      >
        <Table stickyHeader {...getTableProps()}>
          <TableHead>
            {headerGroups.map((headerGroup) => (
              <TableRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column: any) => (
                  <TableCell
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    sx={{
                      fontWeight: "600",
                      color: "#4B465C",
                      width: column.width,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span>{column.render("Header")}</span>
                      {column.canSort && <ColumnSort column={column} />}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>

          {tableData.length > 0 && !isLocalLoading ? (
            <TableBody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <TableRow {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <TableCell {...cell.getCellProps()} sx={{ fontSize: "12px", py: 1 }}>
                        {cell.render("Cell")}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          ) : isLoading || dashboardLoading || isLocalLoading ? (
            <TableBody>
              {Array.from(new Array(5)).map((_, index) => (
                <TableRow key={`skeleton-row-${index}`}>
                  {columns.map((col, colIndex) => (
                    <TableCell key={`skeleton-cell-${index}-${colIndex}`}>
                      <ShimmerSkeleton height={20} width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                  {hasActiveFilters
                    ? "Nenhum evento encontrado com os filtros aplicados"
                    : "Sem dados para listar"}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Card>
  );
};
